import type { BooleanState } from '@mfw/components';

import { FormSubmitButton, FormProvider } from '@mfw/forms';
import {
  ModalFooter,
  ModalHeader,
  useIntl,
  Button,
  Modal,
} from '@mfw/components';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import React from 'react';

import type { ApiQuestionnaireSubmission } from '../../../../schemas/questionnaireSubmissions.schema';

import {
  useQuestionnaireSubmissionForm,
  QuestionField,
} from '../../../QuestionnaireSubmissionForm';
import { useQuestionnaireSubmissionById } from '../../../../hooks/questionnaireSubmissions';
import messages from './messages';

interface AnswerQuestionnaireModalProps {
  answerModal: BooleanState;
  submissionId: null | string;
}

const AnswerQuestionnaireForm = ({
  answerModal,
  submission,
}: {
  answerModal: BooleanState;
  submission: ApiQuestionnaireSubmission;
}) => {
  const { formatMessage } = useIntl();
  const { handleNext, methods, questions } = useQuestionnaireSubmissionForm(
    submission,
    { onSuccess: answerModal.off },
  );
  const formId = React.useId();

  return (
    <FormProvider onSubmit={handleNext} {...methods} id={formId}>
      <Modal
        onClose={answerModal.off}
        variant="medium"
        initiallyOpen
        withoutCloseIcon
      >
        <ModalHeader>
          <Typography variant="h5">{submission.questionnaire.title}</Typography>
        </ModalHeader>
        <Box flexDirection="column" display="flex" gap={2} p={2}>
          {submission.questionnaire.description && (
            <Typography color="text.secondary" variant="body2">
              {submission.questionnaire.description}
            </Typography>
          )}
          {questions.map((question, index) => (
            <QuestionField
              question={question}
              index={index}
              key={question.id}
            />
          ))}
        </Box>
        <ModalFooter>
          <Box justifyContent="flex-end" display="flex" gap={2}>
            <Button
              data-testid="answer-questionnaire-cancel"
              onClick={answerModal.off}
              variant="text"
            >
              {formatMessage(messages.cancel)}
            </Button>
            <FormSubmitButton
              data-testid="answer-questionnaire-submit"
              variant="contained"
              form={formId}
            >
              {formatMessage(messages.submit)}
            </FormSubmitButton>
          </Box>
        </ModalFooter>
      </Modal>
    </FormProvider>
  );
};

export const AnswerQuestionnaireModal = ({
  answerModal,
  submissionId,
}: AnswerQuestionnaireModalProps) => {
  const { data: submission, isLoading } = useQuestionnaireSubmissionById({
    enabled: answerModal.isActive,
    id: submissionId,
  });

  if (!answerModal.isActive) return null;

  if (isLoading || !submission) {
    return (
      <Modal
        onClose={answerModal.off}
        variant="medium"
        initiallyOpen
        withoutCloseIcon
      >
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </Modal>
    );
  }

  return (
    <AnswerQuestionnaireForm
      answerModal={answerModal}
      submission={submission}
    />
  );
};
